/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { trackEvent, trackUserIdentity } from '@/lib/gtm';
import { trackHubspotLogin, clearHubspotLoginSession } from '@/services/hubspotService';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';

export type UserProfile = {
    id: string;
    email: string | null;
    full_name: string | null;
    avatar_url: string | null;
    telefone: string | null;
    cargo: string | null;
    objetivo_principal: string | null;
    tamanho_empresa: string | null;
    como_conheceu: string | null;
    stripe_customer_id?: string | null;
    subscription_tier?: 'trial' | 'basic' | 'pro' | 'business' | 'expired' | null;
    subscription_expires_at?: string | null;
    trial_ends_at?: string | null;
    is_special_grant?: boolean | null;
};

type AuthContextType = {
    session: Session | null;
    user: User | null;
    profile: UserProfile | null;
    loading: boolean;
    hasPassword: boolean;
    linkedProviders: string[];
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    refreshProviders: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [linkedProviders, setLinkedProviders] = useState<string[]>([]);
    const [hasPassword, setHasPassword] = useState(false);
    const queryClient = useQueryClient();

    const fetchProviders = async () => {
        const { data } = await supabase.auth.getUserIdentities();
        if (data?.identities) {
            const providers = data.identities.map((id) => id.provider);
            setLinkedProviders(providers);
            setHasPassword(providers.includes('email'));
        } else {
            setLinkedProviders([]);
            setHasPassword(false);
        }
    };

    const fetchProfile = async (userId: string): Promise<UserProfile | null> => {
        const CACHE_KEY = `personaLab_profile_${userId}`;
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                // Se o perfil não existir na tabela (ex: conta legada ou recém vinculada), cria e sincroniza imediatamente
                if (error.code === 'PGRST116') {
                    console.log('Profile does not exist in profiles table, auto-creating for user:', userId);
                    const { data: userData } = await supabase.auth.getUser();
                    const userObj = userData?.user;
                    if (userObj) {
                        const createdAt = userObj.created_at ? new Date(userObj.created_at) : new Date();
                        const trialEnd = new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000);
                        const isTrialPassed = trialEnd < new Date();

                        const newProfile = {
                            id: userId,
                            email: userObj.email || null,
                            full_name: (userObj.user_metadata?.full_name || userObj.user_metadata?.name || null) as string | null,
                            avatar_url: (userObj.user_metadata?.avatar_url || userObj.user_metadata?.picture || null) as string | null,
                            subscription_tier: isTrialPassed ? ('expired' as const) : ('trial' as const),
                            trial_ends_at: trialEnd.toISOString(),
                            is_special_grant: false,
                        };

                        const { data: inserted, error: insertError } = await supabase
                            .from('profiles')
                            .insert(newProfile)
                            .select()
                            .single();

                        if (!insertError && inserted) {
                            setProfile(inserted);
                            localStorage.setItem(CACHE_KEY, JSON.stringify(inserted));
                            return inserted;
                        }
                    }
                }

                console.error('Error fetching profile from Supabase:', error);
                // Fallback to cache on error
                const cached = localStorage.getItem(CACHE_KEY);
                if (cached) {
                    console.log('Using cached profile due to fetch error');
                    const parsed = JSON.parse(cached);
                    setProfile(parsed);
                    return parsed;
                }
                return null;
            }
            if (data) {
                setProfile(data);
                // Update cache
                localStorage.setItem(CACHE_KEY, JSON.stringify(data));
                return data;
            }
            return null;
        } catch (err) {
            console.error('Unexpected error fetching profile:', err);
            // Fallback to cache on unexpected error
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                console.log('Using cached profile due to unexpected error');
                const parsed = JSON.parse(cached);
                setProfile(parsed);
                return parsed;
            }
            return null;
        }
    };

    const handleSessionRefresh = async (currentSession: Session | null) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user?.id) {
            const userId = currentSession.user.id;
            const CACHE_KEY = `personaLab_profile_${userId}`;
            const cached = localStorage.getItem(CACHE_KEY);
            let hasCachedProfile = false;

            if (cached) {
                try {
                    const parsed = JSON.parse(cached);
                    if (parsed && parsed.id === userId) {
                        setProfile(parsed);
                        hasCachedProfile = true;
                    }
                } catch {
                    // Ignora erro de parse de cache
                }
            }

            if (!hasCachedProfile) {
                setLoading(true);
            }

            await fetchProviders();
            const profileData = await fetchProfile(userId);
            trackUserIdentity(currentSession.user, profileData ?? undefined);
        } else {
            setProfile(null);
            setLinkedProviders([]);
            setHasPassword(false);
        }
        setLoading(false);
    };

    const refreshProfile = async () => {
        if (user?.id) {
            await fetchProfile(user.id);
        }
    };

    const refreshProviders = async () => {
        if (user?.id) {
            await fetchProviders();
        }
    };

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            handleSessionRefresh(session);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN') {
                localStorage.removeItem('skipOnboarding');
                if (session?.user?.id) {
                    sessionStorage.removeItem(`personalab_welcome_seen_${session.user.id}`);
                }
                if (session?.user?.email) {
                    trackHubspotLogin({
                        email: session.user.email,
                        userId: session.user.id,
                    });
                }
            }
            if (event === 'SIGNED_OUT') {
                clearHubspotLoginSession();
                queryClient.clear();
                sessionStorage.clear();
            }
            handleSessionRefresh(session);
        });

        return () => subscription.unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signOut = async () => {
        trackEvent('logout');
        clearHubspotLoginSession(user?.id || user?.email || undefined);
        await supabase.auth.signOut();
        setProfile(null);
        setLinkedProviders([]);
        setHasPassword(false);
        queryClient.clear();
    };

    return (
        <AuthContext.Provider value={{ session, user, profile, loading, hasPassword, linkedProviders, signOut, refreshProfile, refreshProviders }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
